import { Crosshair } from '@phosphor-icons/react'
import clsx from 'clsx'
import Link from 'next/link'
import { BardText, VideoHeroBlock } from '../../@types/statamic'
import Container from '../../components/layout/Container'
import { PageInfo } from '@/src/@types/models'
import Bard from '@/src/components/bard/Bard'
import Button from '@/src/components/core/Button'
import CustomLink from '@/src/components/elements/CustomLink'
import JsonLd from '@/src/components/elements/JsonLd'
import StarRating from '@/src/components/elements/StarRating'
import Title from '@/src/components/elements/Title'
import styles from '@/src/css/Masks.module.css'
import locateMeService from '@/src/lib/locateMeService'
import siteInfo, { getBusinessType } from '@/src/lib/siteInfo'

export function getTitle({ title }: VideoHeroBlock) {
  return title
}

export function getDescription({ hero_content }: VideoHeroBlock) {
  const bardText = hero_content.find((bard) => bard.type === 'text') as BardText
  if (bardText) {
    const pTag = bardText.text.match(/.*?<p>(.*?)<\/p>/)
    if (pTag) {
      return pTag[1]
    }
  }
  return undefined
}

export default function Component({
  title,
  heading,
  hero_content,
  thumbnail,
  video,
  link_link,
  link_text,
  locate_me_button,
  google_rating,
  pageInfo,
}: VideoHeroBlock & { pageInfo: PageInfo }) {
  const locateMe = () => {
    const interactiveMap = document.getElementById('interactive_map-1')
    if (interactiveMap) {
      interactiveMap.scrollIntoView({ behavior: 'smooth' })
      locateMeService.locateMe()
    }
  }

  let videoType = 'video/mp4'

  if (video.endsWith('.webm')) {
    videoType = 'video/webm'
  }
  // possibly more types in the future

  return (
    <div className="relative mt-[-30px] overflow-x-clip md:pb-20 xl:-mt-12 xl:min-h-[500px]">
      {pageInfo && google_rating && (
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': getBusinessType(pageInfo.fullUrl),
            name: siteInfo.orgName,
            description: siteInfo.locationName(google_rating.place),
            address: {
              '@type': 'PostalAddress',
              streetAddress: google_rating.street,
              addressLocality: google_rating.place,
              postalCode: google_rating.zip,
            },
            aggregateRating:
              google_rating.rating && google_rating.rating > 0
                ? {
                    '@type': 'AggregateRating',
                    ratingValue: google_rating.rating,
                    ratingCount: google_rating.rating_count,
                    bestRating: 5,
                    worstRating: 1,
                  }
                : undefined,
            geo:
              google_rating.latitude && google_rating.longitude
                ? {
                    '@type': 'GeoCoordinates',
                    latitude: google_rating.latitude,
                    longitude: google_rating.longitude,
                  }
                : undefined,
            // priceRange: courses[0] ? `${courses[0].price} CHF` : undefined,
            // hasMap: `https://g.page/${gmbShortname}`,
            openingHours: ['Mo,Tu,We,Th,Fr 09:00-17:00', 'Sa 09:00-12:00'],
            telephone: google_rating.phone,
            logo: pageInfo.settings.logo?.permalink,
            url: pageInfo.fullUrl,
            image: pageInfo.socialImage
              ? [pageInfo.socialImage?.permalink]
              : undefined,
          }}
        />
      )}
      <div>
        <Container fullPageOnMobile>
          <div className="flex flex-col md:flex-row">
            <div
              className={`relative z-10 order-2 mt-4 w-full px-7 pt-6 md:order-1 md:mt-0 md:max-w-sm md:px-0 md:pt-12 lg:max-w-xl`}
            >
              <Title style={1}>{title}</Title>
              <Title style="headerCompact" className="mt-4 lg:mt-8">
                {heading}
              </Title>
              <Bard
                content={hero_content}
                className="mt-4 md:mt-5 lg:mt-7"
                fullWidth
              />
              {link_link?.url && link_text && !locate_me_button && (
                <CustomLink href={link_link.url} passHref>
                  <Button as="a" className="mt-7 md:mt-7 lg:mt-10">
                    {link_text}
                  </Button>
                </CustomLink>
              )}
              {locate_me_button && link_text && (
                <Button
                  className="mt-7 md:mt-7 lg:mt-10"
                  icon={<Crosshair />}
                  onClick={() => locateMe()}
                >
                  {link_text}
                </Button>
              )}
            </div>
            <div className="relative order-1 w-full flex-1 md:order-2 md:w-auto">
              <div className="aspect-w-3 relative aspect-h-2 md:hidden">
                <video
                  playsInline
                  autoPlay
                  muted
                  loop
                  preload="auto"
                  poster={thumbnail.permalink}
                  webkit-playsinline="true"
                  className={clsx([
                    'size-full object-cover object-center',
                    styles['mask'],
                    styles['mask-8'],
                  ])}
                >
                  <source src={video} type={videoType} />
                  Your browser does not support HTML video.
                </video>
              </div>
              <div className="absolute top-6 right-0 -bottom-8 -left-28 hidden overflow-hidden md:block">
                <video
                  playsInline
                  autoPlay
                  muted
                  loop
                  preload="auto"
                  poster={thumbnail.permalink}
                  webkit-playsinline="true"
                  className="size-full rounded-[16px] object-cover object-center"
                >
                  <source src={video} type={videoType} />
                  Your browser does not support HTML video.
                </video>
                <div className="absolute inset-x-0 -inset-y-40">
                  <div className="size-full bg-[linear-gradient(90deg,_#FFF_0%,_rgba(255,_255,_255,_0.6)_20%,_rgba(255,_255,_255,_0.3)_30%,_rgba(255,_255,_255,_0)_40%)]" />
                </div>
              </div>
              {google_rating &&
                !!google_rating.rating &&
                google_rating.rating > 0 && (
                  <>
                    {(google_rating.rating_url && (
                      <Link
                        href={google_rating.rating_url}
                        className="absolute right-4 -bottom-4 z-10 rounded-lg border border-blue-100 bg-blue-50 px-4 py-2 shadow-sm md:right-6 md:-bottom-8 md:translate-y-1/2"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <StarRating rating={google_rating.rating} />
                        <p className="mt-1 text-xs md:text-sm">
                          {google_rating.rating_count} Bewertungen auf Google
                        </p>
                      </Link>
                    )) || (
                      <div className="absolute right-4 -bottom-4 z-10 rounded-lg border border-blue-100 bg-blue-50 px-4 py-2 shadow-sm md:right-6 md:-bottom-8 md:translate-y-1/2">
                        <StarRating rating={google_rating.rating} />
                        <p className="mt-1 text-xs md:text-sm">
                          {google_rating.rating_count} Bewertungen auf Google
                        </p>
                      </div>
                    )}
                  </>
                )}
            </div>
          </div>
        </Container>
      </div>
    </div>
  )
}
